import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertCircle,
  BarChart3,
  Check,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Shield,
  Trash2,
  Undo2,
  Users,
  X,
} from 'lucide-react';

import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import {
  FEATURE_POLICY_FIELDS,
  ORGANIZATION_ROLE_OPTIONS,
  type BillingPolicy,
  type FeatureKey,
  type FeatureOverrideMode,
  type FeaturePolicy,
  type OrganizationMember,
  type OrganizationMemberUsage,
  type OrganizationRole,
  type SettingsOverviewResponse,
  authorizedJsonRequest,
  capabilityPayloadFromModes,
  featureOverrideMode,
  formatBytes,
  formatCredits,
  formatCurrency,
  formatDateTime,
  formatNumber,
  formatRelativeTime,
  roleLabel,
} from './permissionsShared';

interface MemberDraft {
  email: string;
  name: string;
  role: OrganizationRole;
  capabilityModes: Partial<Record<FeatureKey, FeatureOverrideMode>>;
}

interface MemberBillingDraft {
  isUnlimited: boolean;
  monthlyCreditLimit: string;
  monthlyUsdLimit: string;
}

interface YouTubeAuthStatus {
  authenticated: boolean;
  tokenExpiry: string | null;
  scopes: string[];
  canEditVideos: boolean;
  canPostComments: boolean;
  canReadVideoMetadata: boolean;
}

const DEFAULT_ROLE: OrganizationRole = 'reader';
type SettingsPaneId = 'overview' | 'personal' | 'billing' | 'members' | 'policy' | 'activity';

const SETTINGS_PANES: Array<{
  id: SettingsPaneId;
  title: string;
  description: string;
}> = [
  {
    id: 'overview',
    title: 'Overview',
    description: 'Health, spend posture, and organization-wide signals.',
  },
  {
    id: 'personal',
    title: 'Personal & YouTube',
    description: 'Your usage footprint and your YouTube auth connection.',
  },
  {
    id: 'billing',
    title: 'Billing & Credits',
    description: 'Monthly defaults, credit mapping, and budget policy.',
  },
  {
    id: 'members',
    title: 'Members',
    description: 'Roles, removals, overrides, and per-member activity.',
  },
  {
    id: 'policy',
    title: 'Feature Policy',
    description: 'Global access gates across chat, agent, CLI, and exports.',
  },
  {
    id: 'activity',
    title: 'Activity Feed',
    description: 'Cross-org event drilldowns by user, provider, and operation.',
  },
];

const USAGE_RANGE_OPTIONS = [7, 30, 90, 180] as const;
const ACTIVITY_RANGE_OPTIONS = [7, 14, 30, 90] as const;

function buildCapabilityModes(
  capabilities: OrganizationMember['capabilities']
): Partial<Record<FeatureKey, FeatureOverrideMode>> {
  return FEATURE_POLICY_FIELDS.reduce<Partial<Record<FeatureKey, FeatureOverrideMode>>>(
    (acc, feature) => {
      acc[feature.key] = featureOverrideMode(capabilities?.[feature.key]);
      return acc;
    },
    {}
  );
}

function buildMemberDraft(member: OrganizationMember): MemberDraft {
  return {
    email: member.email,
    name: member.name || member.email,
    role: member.role,
    capabilityModes: buildCapabilityModes(member.capabilities),
  };
}

function buildMemberBillingDraft(member: OrganizationMember): MemberBillingDraft {
  const override = member.billing?.override;
  return {
    isUnlimited: !!override?.isUnlimited,
    monthlyCreditLimit:
      override?.monthlyCreditLimit !== null && override?.monthlyCreditLimit !== undefined
        ? String(override.monthlyCreditLimit)
        : '',
    monthlyUsdLimit:
      override?.monthlyUsdLimit !== null && override?.monthlyUsdLimit !== undefined
        ? String(override.monthlyUsdLimit)
        : '',
  };
}

function createEmptyMemberDraft(): MemberDraft {
  return {
    email: '',
    name: '',
    role: DEFAULT_ROLE,
    capabilityModes: FEATURE_POLICY_FIELDS.reduce<Partial<Record<FeatureKey, FeatureOverrideMode>>>(
      (acc, feature) => {
        acc[feature.key] = 'inherit';
        return acc;
      },
      {}
    ),
  };
}

function permissionCount(policy: FeaturePolicy | undefined): number {
  if (!policy) return 0;
  return FEATURE_POLICY_FIELDS.filter((feature) => policy[feature.key] === true).length;
}

function normalizeDraftEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizeBillingInput(value: string): string {
  return value.trim();
}

function normalizeNullableNumber(value: string): number | null {
  const normalized = normalizeBillingInput(value);
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function draftsMatch(left: MemberDraft, right: MemberDraft): boolean {
  if (left.role !== right.role) return false;
  return FEATURE_POLICY_FIELDS.every(
    (feature) => (left.capabilityModes[feature.key] || 'inherit') === (right.capabilityModes[feature.key] || 'inherit')
  );
}

function billingDraftsMatch(left: MemberBillingDraft, right: MemberBillingDraft): boolean {
  return left.isUnlimited === right.isUnlimited
    && normalizeBillingInput(left.monthlyCreditLimit) === normalizeBillingInput(right.monthlyCreditLimit)
    && normalizeBillingInput(left.monthlyUsdLimit) === normalizeBillingInput(right.monthlyUsdLimit);
}

function buildPendingSummary({
  policyDirty,
  changedMembers,
  newMembers,
  removals,
}: {
  policyDirty: boolean;
  changedMembers: number;
  newMembers: number;
  removals: number;
}): string {
  const parts = [];
  if (policyDirty) parts.push('organization policy');
  if (changedMembers) parts.push(`${changedMembers} member update${changedMembers === 1 ? '' : 's'}`);
  if (newMembers) parts.push(`${newMembers} staged addition${newMembers === 1 ? '' : 's'}`);
  if (removals) parts.push(`${removals} removal${removals === 1 ? '' : 's'}`);
  return parts.join(', ');
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0%';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0%';
  return `${Math.round(numeric)}%`;
}

function usageStatusLabel(status: string | null | undefined): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'unknown';
  return normalized.replace(/_/g, ' ');
}

function formatExpiryCountdown(value: string | null | undefined): string {
  if (!value) return 'No expiry recorded';
  const expiry = new Date(value);
  if (Number.isNaN(expiry.getTime())) return value;
  const diffMs = expiry.getTime() - Date.now();
  if (diffMs <= 0) return 'Expired';
  const hours = diffMs / (60 * 60 * 1000);
  if (hours < 1) return `${Math.max(1, Math.round(diffMs / (60 * 1000)))} min remaining`;
  if (hours < 48) return `${Math.round(hours)} hr remaining`;
  return `${Math.round(hours / 24)} day${Math.round(hours / 24) === 1 ? '' : 's'} remaining`;
}

const PermissionSettingsPage: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const { token } = useAuth();
  const [usageDays, setUsageDays] = useState(30);
  const [activityDays, setActivityDays] = useState(14);
  const [activePane, setActivePane] = useState<SettingsPaneId>('overview');
  const [overview, setOverview] = useState<SettingsOverviewResponse | null>(null);
  const [policyDraft, setPolicyDraft] = useState<FeaturePolicy | null>(null);
  const [billingPolicyDraft, setBillingPolicyDraft] = useState<BillingPolicy | null>(null);
  const [memberDrafts, setMemberDrafts] = useState<Record<string, MemberDraft>>({});
  const [memberBillingDrafts, setMemberBillingDrafts] = useState<Record<string, MemberBillingDraft>>({});
  const [stagedNewMembers, setStagedNewMembers] = useState<Record<string, MemberDraft>>({});
  const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);
  const [inviteDraft, setInviteDraft] = useState<MemberDraft>(createEmptyMemberDraft());
  const [expandedMembers, setExpandedMembers] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [youtubeAuthStatus, setYoutubeAuthStatus] = useState<YouTubeAuthStatus | null>(null);
  const [youtubeAuthLoading, setYoutubeAuthLoading] = useState(false);
  const [youtubeAuthBusy, setYoutubeAuthBusy] = useState(false);
  const [youtubeAuthError, setYoutubeAuthError] = useState('');

  const canManageOrg = !!overview?.accessProfile.permissions.organization.manage;
  const canViewUsage = !!overview?.accessProfile.permissions.organization.viewUsage;
  const canGrantMaster = overview?.accessProfile.membership.role === 'master';
  const currentUserEmail = overview?.accessProfile.user.email || '';

  const roleOptions = useMemo(
    () =>
      ORGANIZATION_ROLE_OPTIONS.filter((option) =>
        option.value === 'master' ? canGrantMaster : true
      ),
    [canGrantMaster]
  );

  const pendingRemovalSet = useMemo(
    () => new Set(pendingRemovals.map(normalizeDraftEmail)),
    [pendingRemovals]
  );

  const stagedNewMemberList = useMemo(
    () => Object.values(stagedNewMembers).sort((left, right) => left.email.localeCompare(right.email)),
    [stagedNewMembers]
  );

  const changedExistingMembers = useMemo(() => {
    if (!overview) return [];
    return overview.organization.members.filter((member) => {
      if (pendingRemovalSet.has(normalizeDraftEmail(member.email))) return false;
      const draft = memberDrafts[member.email];
      if (!draft) return false;
      return !draftsMatch(draft, buildMemberDraft(member));
    });
  }, [memberDrafts, overview, pendingRemovalSet]);

  const changedBillingMembers = useMemo(() => {
    if (!overview) return [];
    return overview.organization.members.filter((member) => {
      if (pendingRemovalSet.has(normalizeDraftEmail(member.email))) return false;
      const draft = memberBillingDrafts[member.email];
      if (!draft) return false;
      return !billingDraftsMatch(draft, buildMemberBillingDraft(member));
    });
  }, [memberBillingDrafts, overview, pendingRemovalSet]);

  const policyDirty = useMemo(() => {
    if (!overview || !policyDraft) return false;
    return FEATURE_POLICY_FIELDS.some(
      (feature) => overview.organization.featurePolicy?.[feature.key] !== policyDraft[feature.key]
    );
  }, [overview, policyDraft]);

  const billingPolicyDirty = useMemo(() => {
    if (!overview?.organization.billing?.policy || !billingPolicyDraft) return false;
    return JSON.stringify(overview.organization.billing.policy) !== JSON.stringify(billingPolicyDraft);
  }, [billingPolicyDraft, overview]);

  const pendingSummary = useMemo(
    () =>
      buildPendingSummary({
        policyDirty: policyDirty || billingPolicyDirty,
        changedMembers: new Set(
          [...changedExistingMembers, ...changedBillingMembers].map((member) =>
            normalizeDraftEmail(member.email)
          )
        ).size,
        newMembers: stagedNewMemberList.length,
        removals: pendingRemovals.length,
      }),
    [
      billingPolicyDirty,
      changedBillingMembers,
      changedExistingMembers,
      pendingRemovals.length,
      policyDirty,
      stagedNewMemberList.length,
    ]
  );

  const hasPendingChanges = !!(
    policyDirty
    || billingPolicyDirty
    || changedExistingMembers.length
    || changedBillingMembers.length
    || stagedNewMemberList.length
    || pendingRemovals.length
  );

  const paneMeta = SETTINGS_PANES.find((pane) => pane.id === activePane) || SETTINGS_PANES[0];
  const orgBilling = overview?.organization.billing || null;
  const personalBilling = overview?.accessProfile.billing || null;
  const memberEmailSet = useMemo(
    () => new Set((overview?.organization.members || []).map((member) => member.email.trim().toLowerCase())),
    [overview?.organization.members],
  );

  const personalProviderLeaders = useMemo(() => {
    if (!personalBilling?.byProvider) return [];
    return personalBilling.byProvider
      .filter((row) => (row.totalCostUsd || 0) > 0 || (row.totalCreditsUsed || 0) > 0 || row.events > 0)
      .slice(0, 6)
      .map((row) => ({
        label: `${row.provider}/${row.service}`,
        value: row.totalCostUsd,
        secondaryValue: row.totalCreditsUsed,
        detail: `${formatNumber(row.events)} events`,
      }));
  }, [personalBilling]);

  const orgProviderLeaders = useMemo(() => {
    if (!orgBilling?.byProvider) return [];
    return orgBilling.byProvider
      .filter((row) => (row.totalCostUsd || 0) > 0 || (row.totalCreditsUsed || 0) > 0 || row.events > 0)
      .slice(0, 8)
      .map((row) => ({
        label: `${row.provider}/${row.service}`,
        value: row.totalCostUsd,
        secondaryValue: row.totalCreditsUsed,
        detail: `${formatNumber(row.events)} events`,
      }));
  }, [orgBilling]);

  const orgPrincipalLeaders = useMemo(() => {
    const memberItems: Array<{ label: string; value: number; secondaryValue?: number; detail?: string }> = [];
    const otherItems: Array<{ label: string; value: number; secondaryValue?: number; detail?: string }> = [];

    if (!orgBilling?.byUser) {
      return { memberItems, otherItems };
    }

    orgBilling.byUser
      .filter((row) => (row.totalCostUsd || 0) > 0 || (row.totalCreditsUsed || 0) > 0 || row.events > 0)
      .forEach((row) => {
        const principal = (row.userEmail || '').trim().toLowerCase();
        const item = {
          label: row.userEmail || 'Unknown principal',
          value: row.totalCostUsd,
          secondaryValue: row.totalCreditsUsed,
          detail: `${formatNumber(row.events)} events`,
        };

        if (principal && (memberEmailSet.has(principal) || principal.includes('@'))) {
          memberItems.push(item);
        } else {
          otherItems.push(item);
        }
      });

    return {
      memberItems: memberItems.slice(0, 8),
      otherItems: otherItems.slice(0, 6),
    };
  }, [memberEmailSet, orgBilling]);

  const orgUserLeaders = orgPrincipalLeaders.memberItems;

  const organizationUsagePrincipalLeaders = useMemo(() => {
    const memberItems: Array<{ label: string; value: number; secondaryValue?: number; detail?: string }> = [];
    const otherItems: Array<{ label: string; value: number; secondaryValue?: number; detail?: string }> = [];

    if (!overview?.usage) {
      return { memberItems, otherItems };
    }

    overview.usage.summary.byUser.forEach((row) => {
      const principal = (row.user_email || '').trim().toLowerCase();
      const item = {
        label: row.user_email || 'Unknown principal',
        value: row.total_cost_usd || 0,
        secondaryValue: row.total_credits_used || 0,
        detail: `${formatNumber(row.events)} events`,
      };

      if (principal && (memberEmailSet.has(principal) || principal.includes('@'))) {
        memberItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    return {
      memberItems: memberItems.slice(0, 10),
      otherItems: otherItems.slice(0, 10),
    };
  }, [memberEmailSet, overview]);

  const memberUsageLeaders = useMemo(() => {
    if (!overview) return [];
    return [...overview.organization.members]
      .sort((left, right) => {
        const rightUsage = right.usage?.totalCostUsd || right.billing?.totalCostUsd || 0;
        const leftUsage = left.usage?.totalCostUsd || left.billing?.totalCostUsd || 0;
        return rightUsage - leftUsage;
      })
      .slice(0, 8)
      .map((member) => ({
        label: member.name || member.email,
        value: member.usage?.totalCostUsd || member.billing?.totalCostUsd || 0,
        secondaryValue: member.billing?.totalCreditsUsed || member.usage?.totalCreditsUsed || 0,
        detail: member.usage?.lastUsedAt
          ? `Last used ${formatRelativeTime(member.usage.lastUsedAt)}`
          : member.lastLoginAt
            ? `Last login ${formatRelativeTime(member.lastLoginAt)}`
            : 'No recent tracked activity',
      }));
  }, [overview]);

  const applyUsageRange = (nextDays: number) => {
    setUsageDays(nextDays);
    void loadOverview(nextDays, false, hasPendingChanges, activityDays);
  };

  const applyActivityRange = (nextDays: number) => {
    setActivityDays(nextDays);
    void loadOverview(usageDays, false, hasPendingChanges, nextDays);
  };

  function resetEditorState(response: SettingsOverviewResponse) {
    setPolicyDraft(response.organization.featurePolicy);
    setBillingPolicyDraft(response.organization.billing?.policy || null);
    setMemberDrafts(
      Object.fromEntries(
        response.organization.members.map((member) => [member.email, buildMemberDraft(member)])
      )
    );
    setMemberBillingDrafts(
      Object.fromEntries(
        response.organization.members.map((member) => [member.email, buildMemberBillingDraft(member)])
      )
    );
    setStagedNewMembers({});
    setPendingRemovals([]);
    setInviteDraft(createEmptyMemberDraft());
  }

  const loadYouTubeAuthStatus = useCallback(async () => {
    if (!token) return;
    setYoutubeAuthLoading(true);
    setYoutubeAuthError('');
    try {
      const response = await authorizedJsonRequest<YouTubeAuthStatus>(
        '/api/v1/youtube/auth/status',
        token
      );
      setYoutubeAuthStatus(response);
    } catch (err) {
      setYoutubeAuthError(err instanceof Error ? err.message : 'Failed to load YouTube auth status');
    } finally {
      setYoutubeAuthLoading(false);
    }
  }, [token]);

  const startYouTubeAuth = useCallback(async () => {
    if (!token || typeof window === 'undefined') return;
    setYoutubeAuthBusy(true);
    setYoutubeAuthError('');
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}?pane=personal`;
      const response = await authorizedJsonRequest<{ authUrl: string }>(
        `/api/v1/youtube/auth/url?returnUrl=${encodeURIComponent(returnUrl)}`,
        token
      );
      window.location.assign(response.authUrl);
    } catch (err) {
      setYoutubeAuthBusy(false);
      setYoutubeAuthError(err instanceof Error ? err.message : 'Failed to start YouTube authentication');
    }
  }, [token]);

  const disconnectYouTubeAuth = useCallback(async () => {
    if (!token) return;
    setYoutubeAuthBusy(true);
    setYoutubeAuthError('');
    try {
      await authorizedJsonRequest<{ success: boolean }>(
        '/api/v1/youtube/auth',
        token,
        { method: 'DELETE' }
      );
      setSuccess('YouTube OAuth credentials removed.');
      await loadYouTubeAuthStatus();
    } catch (err) {
      setYoutubeAuthError(err instanceof Error ? err.message : 'Failed to disconnect YouTube');
    } finally {
      setYoutubeAuthBusy(false);
    }
  }, [loadYouTubeAuthStatus, token]);

  const loadOverview = async (
    days = usageDays,
    hardRefresh = false,
    preserveDrafts = false,
    chartDays = activityDays
  ) => {
    if (!token) return;

    if (hardRefresh) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      setError('');
      const response = await authorizedJsonRequest<SettingsOverviewResponse>(
        `/api/settings/overview?days=${days}&activityDays=${chartDays}`,
        token
      );
      setOverview(response);
      if (!preserveDrafts) {
        resetEditorState(response);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    void loadOverview(usageDays, true, false, activityDays);
    void loadYouTubeAuthStatus();
  }, [token, loadYouTubeAuthStatus]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const requestedPane = params.get('pane');
    if (requestedPane && SETTINGS_PANES.some((pane) => pane.id === requestedPane)) {
      setActivePane(requestedPane as SettingsPaneId);
    }
    const status = params.get('youtube_auth');
    const message = params.get('message');
    if (!status) return;

    if (status === 'success') {
      setSuccess('YouTube account connected.');
      void loadYouTubeAuthStatus();
    } else if (status === 'error') {
      setYoutubeAuthError(message || 'YouTube authentication failed');
    }

    params.delete('youtube_auth');
    params.delete('message');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, [loadYouTubeAuthStatus]);

  const updateMemberDraft = (
    email: string,
    updater: (current: MemberDraft) => MemberDraft
  ) => {
    setMemberDrafts((current) => {
      const existing = current[email];
      if (!existing) return current;
      return {
        ...current,
        [email]: updater(existing),
      };
    });
  };

  const updateMemberBillingDraft = (
    email: string,
    updater: (current: MemberBillingDraft) => MemberBillingDraft
  ) => {
    setMemberBillingDrafts((current) => {
      const existing = current[email];
      if (!existing) return current;
      return {
        ...current,
        [email]: updater(existing),
      };
    });
  };

  const updateBillingRate = (index: number, creditsPerUsd: string) => {
    setBillingPolicyDraft((current) => {
      if (!current) return current;
      const nextRates = current.serviceCreditRates.map((rate, rateIndex) =>
        rateIndex === index
          ? {
              ...rate,
              creditsPerUsd:
                normalizeNullableNumber(creditsPerUsd) ?? rate.creditsPerUsd,
            }
          : rate
      );
      return {
        ...current,
        serviceCreditRates: nextRates,
      };
    });
  };

  const updateInviteDraftMode = (key: FeatureKey, value: FeatureOverrideMode) => {
    setInviteDraft((current) => ({
      ...current,
      capabilityModes: { ...current.capabilityModes, [key]: value },
    }));
  };

  const stageInviteDraft = () => {
    const email = normalizeDraftEmail(inviteDraft.email);
    if (!email) {
      setError('Member email is required.');
      return;
    }
    if (overview?.organization.members.some((member) => normalizeDraftEmail(member.email) === email)) {
      setError('That member already exists. Edit them in the member list below.');
      return;
    }
    if (pendingRemovalSet.has(email)) {
      setError('That user is currently queued for removal. Undo the removal before re-adding them.');
      return;
    }

    setError('');
    setSuccess('');
    setStagedNewMembers((current) => ({
      ...current,
      [email]: {
        ...inviteDraft,
        email,
        name: inviteDraft.name.trim() || email,
      },
    }));
    setInviteDraft(createEmptyMemberDraft());
  };

  const editStagedMember = (email: string) => {
    const staged = stagedNewMembers[email];
    if (!staged) return;
    setInviteDraft(staged);
    setStagedNewMembers((current) => {
      const next = { ...current };
      delete next[email];
      return next;
    });
  };

  const queueRemoval = (email: string) => {
    const normalizedEmail = normalizeDraftEmail(email);
    if (!normalizedEmail) return;
    setPendingRemovals((current) =>
      current.includes(normalizedEmail)
        ? current.filter((item) => item !== normalizedEmail)
        : [...current, normalizedEmail]
    );
  };

  const discardChanges = () => {
    if (!overview) return;
    resetEditorState(overview);
    setError('');
    setSuccess('Discarded unsaved changes.');
  };

  const saveAllChanges = async () => {
    if (!token || !overview || !canManageOrg) return;

    const removalEmails = overview.organization.members
      .map((member) => normalizeDraftEmail(member.email))
      .filter((email) => pendingRemovalSet.has(email));
    const changedMemberPayloads = changedExistingMembers.map((member) => {
      const draft = memberDrafts[member.email];
      const billingDraft = memberBillingDrafts[member.email] || buildMemberBillingDraft(member);
      return {
        email: normalizeDraftEmail(draft.email),
        name: draft.name.trim() || normalizeDraftEmail(draft.email),
        role: draft.role,
        capabilities: capabilityPayloadFromModes(draft.capabilityModes),
        billing: {
          isUnlimited: billingDraft.isUnlimited,
          monthlyCreditLimit: normalizeNullableNumber(billingDraft.monthlyCreditLimit),
          monthlyUsdLimit: normalizeNullableNumber(billingDraft.monthlyUsdLimit),
          clear:
            !billingDraft.isUnlimited
            && normalizeNullableNumber(billingDraft.monthlyCreditLimit) === null
            && normalizeNullableNumber(billingDraft.monthlyUsdLimit) === null,
        },
      };
    });
    const changedBillingOnlyPayloads = changedBillingMembers
      .filter((member) => !changedExistingMembers.some((existing) => existing.email === member.email))
      .map((member) => {
        const draft = memberDrafts[member.email] || buildMemberDraft(member);
        const billingDraft = memberBillingDrafts[member.email] || buildMemberBillingDraft(member);
        return {
          email: normalizeDraftEmail(draft.email),
          name: draft.name.trim() || normalizeDraftEmail(draft.email),
          role: draft.role,
          capabilities: capabilityPayloadFromModes(draft.capabilityModes),
          billing: {
            isUnlimited: billingDraft.isUnlimited,
            monthlyCreditLimit: normalizeNullableNumber(billingDraft.monthlyCreditLimit),
            monthlyUsdLimit: normalizeNullableNumber(billingDraft.monthlyUsdLimit),
            clear:
              !billingDraft.isUnlimited
              && normalizeNullableNumber(billingDraft.monthlyCreditLimit) === null
              && normalizeNullableNumber(billingDraft.monthlyUsdLimit) === null,
          },
        };
      });
    const stagedMemberPayloads = stagedNewMemberList.map((draft) => ({
      email: normalizeDraftEmail(draft.email),
      name: draft.name.trim() || normalizeDraftEmail(draft.email),
      role: draft.role,
      capabilities: capabilityPayloadFromModes(draft.capabilityModes),
    }));

    setIsSavingAll(true);
    setError('');
    setSuccess('');

    let appliedOperations = 0;

    try {
      if ((policyDirty || billingPolicyDirty) && policyDraft) {
        await authorizedJsonRequest('/api/settings/organization', token, {
          method: 'PATCH',
          body: JSON.stringify({
            featurePolicy: policyDraft,
            billing: billingPolicyDraft,
          }),
        });
        appliedOperations += 1;
      }

      for (const payload of changedMemberPayloads) {
        await authorizedJsonRequest('/api/settings/members', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        appliedOperations += 1;
      }

      for (const payload of changedBillingOnlyPayloads) {
        await authorizedJsonRequest('/api/settings/members', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        appliedOperations += 1;
      }

      for (const payload of stagedMemberPayloads) {
        await authorizedJsonRequest('/api/settings/members', token, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        appliedOperations += 1;
      }

      for (const email of removalEmails) {
        await authorizedJsonRequest(`/api/settings/members/${encodeURIComponent(email)}`, token, {
          method: 'DELETE',
        });
        appliedOperations += 1;
      }

      await loadOverview(usageDays, false, false);
      setSuccess(
        appliedOperations === 1
          ? 'Saved 1 settings change.'
          : `Saved ${appliedOperations} settings changes.`
      );
    } catch (err) {
      await loadOverview(usageDays, false, false);
      const baseMessage = err instanceof Error ? err.message : 'Failed to save settings';
      setError(
        appliedOperations > 0
          ? `${baseMessage}. The page was refreshed because some changes were already applied.`
          : baseMessage
      );
    } finally {
      setIsSavingAll(false);
    }
  };

  const topSurfaceClass = isDarkMode
    ? 'bg-gray-800 border-gray-700 text-white'
    : 'bg-white border-gray-200 text-gray-900';

  const nestedSurfaceClass = isDarkMode
    ? 'bg-gray-900/70 border-gray-700'
    : 'bg-gray-50 border-gray-200';

  const secondaryTextClass = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const subtleTextClass = isDarkMode ? 'text-gray-500' : 'text-gray-500';
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow ${
    isDarkMode
      ? 'border-gray-600 bg-gray-900 text-gray-100 placeholder:text-gray-500'
      : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'
  }`;

  if (!token) {
    return (
      <div className={`rounded-2xl border p-6 ${topSurfaceClass}`}>
        <p className={secondaryTextClass}>Sign in to load organization settings.</p>
      </div>
    );
  }

  if (isLoading && !overview) {
    return (
      <div className={`rounded-2xl border p-8 ${topSurfaceClass}`}>
        <div className="flex items-center justify-center gap-3">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span className={secondaryTextClass}>Loading settings overview…</span>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className={`rounded-2xl border p-6 ${topSurfaceClass}`}>
        <div className="flex items-start gap-3 text-red-500">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Settings failed to load.</p>
            <p className={`mt-1 text-sm ${secondaryTextClass}`}>{error || 'Unknown error'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(error || success) && (
        <div
          className={`rounded-2xl border p-4 ${
            error
              ? isDarkMode
                ? 'border-red-500/40 bg-red-950/30 text-red-200'
                : 'border-red-200 bg-red-50 text-red-700'
              : isDarkMode
                ? 'border-brand-yellow/40 bg-brand-yellow/15 text-brand-yellow'
                : 'border-[#e7c572] bg-[#fff6e5] text-[#7a560c]'
          }`}
        >
          <div className="flex items-start gap-3">
            {error ? <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> : <Check className="mt-0.5 h-5 w-5 shrink-0" />}
            <span className="text-sm">{error || success}</span>
          </div>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className={`rounded-[32px] border p-5 ${topSurfaceClass} xl:sticky xl:top-6 xl:h-[calc(100vh-3rem)] xl:overflow-hidden`}>
          <div className="flex h-full flex-col gap-5">
            <div className={`rounded-[28px] border p-5 ${nestedSurfaceClass}`}>
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-3 py-1 text-xs font-semibold text-brand-cloud">
                <Shield className="h-3.5 w-3.5" />
                Organization & Security
              </div>
              <h1 className="mt-4 text-2xl font-semibold">{overview.organization.name}</h1>
              <p className={`mt-2 text-sm ${secondaryTextClass}`}>
                Org slug <span className="font-mono">{overview.organization.slug}</span>
              </p>
              <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                You are signed in as {overview.accessProfile.user.email} with {roleLabel(overview.accessProfile.membership.role)} access.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <MiniStatCard
                isDarkMode={isDarkMode}
                label="Members"
                value={formatNumber(overview.organization.members.length)}
                hint={canManageOrg ? 'Roles, overrides, and removals' : 'Read-only directory'}
              />
              <MiniStatCard
                isDarkMode={isDarkMode}
                label="Usage visibility"
                value={canViewUsage ? 'Org-wide' : 'Personal only'}
                hint={canViewUsage ? 'Admin analytics enabled' : 'Request org admin access for org-wide views'}
              />
            </div>

            <div className={`rounded-[28px] border p-4 ${nestedSurfaceClass}`}>
              <p className="text-sm font-medium">Time windows</p>
              <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                Apply these ranges across usage tables and activity graphs.
              </p>
              <div className="mt-4 space-y-4">
                <div>
                  <p className={`mb-2 text-xs uppercase tracking-wide ${subtleTextClass}`}>Usage summary</p>
                  <RangeSegmentControl
                    isDarkMode={isDarkMode}
                    options={USAGE_RANGE_OPTIONS.map((value) => ({ value, label: `${value}d` }))}
                    value={usageDays}
                    onChange={applyUsageRange}
                  />
                </div>
                <div>
                  <p className={`mb-2 text-xs uppercase tracking-wide ${subtleTextClass}`}>Activity graphs</p>
                  <RangeSegmentControl
                    isDarkMode={isDarkMode}
                    options={ACTIVITY_RANGE_OPTIONS.map((value) => ({ value, label: `${value}d` }))}
                    value={activityDays}
                    onChange={applyActivityRange}
                  />
                </div>
              </div>
            </div>

            <nav className="grid gap-2 overflow-auto xl:flex-1 xl:content-start">
              {SETTINGS_PANES.map((pane) => {
                const badge = pane.id === 'members'
                  ? formatNumber(overview.organization.members.length)
                  : pane.id === 'billing' && orgBilling
                    ? formatCurrency(orgBilling.totals.totalCostUsd)
                    : pane.id === 'activity' && overview.usage
                      ? formatNumber(overview.usage.summary.totals.events)
                      : pane.id === 'personal'
                        ? formatCurrency(overview.selfUsage.summary.totals.total_cost_usd)
                        : pane.id === 'policy'
                          ? `${permissionCount(overview.organization.featurePolicy)}/${FEATURE_POLICY_FIELDS.length}`
                          : personalBilling?.isOverLimit
                            ? 'Alert'
                            : 'Live';
                return (
                  <SettingsNavButton
                    key={pane.id}
                    isDarkMode={isDarkMode}
                    title={pane.title}
                    description={pane.description}
                    badge={badge}
                    active={pane.id === activePane}
                    onClick={() => setActivePane(pane.id)}
                  />
                );
              })}
            </nav>
          </div>
        </aside>

        <section className={`overflow-hidden rounded-[36px] border ${topSurfaceClass}`}>
          <div className={`border-b px-5 py-5 md:px-8 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className={`text-xs uppercase tracking-[0.25em] ${subtleTextClass}`}>Settings</p>
                <h2 className="mt-2 text-3xl font-semibold">{paneMeta.title}</h2>
                <p className={`mt-2 max-w-3xl text-sm ${secondaryTextClass}`}>{paneMeta.description}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className={`rounded-full px-3 py-1 text-xs font-medium ${nestedSurfaceClass}`}>
                  Usage {usageDays}d · Activity {activityDays}d
                </div>
                <button
                  type="button"
                  onClick={() => void loadOverview(usageDays, false, hasPendingChanges, activityDays)}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-5 md:p-8">
            {canManageOrg && hasPendingChanges && (
              <section
                className={`sticky top-4 z-10 mb-6 rounded-3xl border p-4 shadow-lg backdrop-blur ${isDarkMode ? 'border-brand-yellow/30 bg-slate-950/90 text-white' : 'border-[#e7c572] bg-white/95 text-gray-900'}`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow px-3 py-1 text-xs font-semibold text-brand-navy">
                      <Save className="h-3.5 w-3.5" />
                      Pending changes
                    </div>
                    <p className="mt-3 text-sm font-medium">
                      {pendingSummary || 'Settings changes are staged and ready to save.'}
                    </p>
                    <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                      Nothing is persisted until you use the global save action.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={discardChanges}
                      disabled={isSavingAll}
                      className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${isSavingAll ? 'cursor-not-allowed opacity-70' : ''}`}
                    >
                      <Undo2 className="h-4 w-4" />
                      Discard Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => void saveAllChanges()}
                      disabled={isSavingAll}
                      className={`inline-flex items-center gap-2 rounded-xl bg-brand-yellow px-4 py-2 text-sm font-medium text-brand-navy transition-colors hover:bg-[#ffc54d] ${isSavingAll ? 'cursor-not-allowed opacity-70' : ''}`}
                    >
                      <Save className="h-4 w-4" />
                      {isSavingAll ? 'Saving Everything…' : 'Save All'}
                    </button>
                  </div>
                </div>
              </section>
            )}

            {activePane === 'overview' && (
              <div className="space-y-6">
                {personalBilling && (
                  <section
                    className={`rounded-[32px] border p-5 ${
                      personalBilling.isOverLimit
                        ? isDarkMode
                          ? 'border-red-500/40 bg-red-950/30 text-red-100'
                          : 'border-red-200 bg-red-50 text-red-800'
                        : personalBilling.isWarning
                          ? isDarkMode
                            ? 'border-amber-500/40 bg-amber-950/30 text-amber-100'
                            : 'border-amber-200 bg-amber-50 text-amber-800'
                          : isDarkMode
                            ? 'border-brand-navy/30 bg-brand-navy/20 text-brand-cloud'
                            : 'border-[#d4dde8] bg-[#eef2f6] text-brand-navy'
                    }`}
                  >
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div>
                        <p className="text-sm font-semibold">
                          {personalBilling.isOverLimit
                            ? 'Monthly allowance exhausted'
                            : personalBilling.isWarning
                              ? 'Monthly allowance nearing limit'
                              : 'Monthly allowance healthy'}
                        </p>
                        <p className="mt-2 max-w-2xl text-sm">
                          {personalBilling.isUnlimited
                            ? 'Your account is unlimited for this organization.'
                            : `This calendar month you have used ${formatCurrency(personalBilling.totalCostUsd)} / ${personalBilling.monthlyUsdLimit !== null ? formatCurrency(personalBilling.monthlyUsdLimit) : 'Unlimited'} and ${formatCredits(personalBilling.totalCreditsUsed)} / ${personalBilling.monthlyCreditLimit !== null ? formatCredits(personalBilling.monthlyCreditLimit) : 'Unlimited'} credits.`}
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px]">
                        <MiniStatCard
                          isDarkMode={isDarkMode}
                          label="Cost used"
                          value={formatCurrency(personalBilling.totalCostUsd)}
                          hint={personalBilling.monthlyUsdLimit !== null ? `${formatPercent(personalBilling.percentUsdUsed)} of limit` : 'Unlimited'}
                        />
                        <MiniStatCard
                          isDarkMode={isDarkMode}
                          label="Credits remaining"
                          value={personalBilling.creditsRemaining === null ? 'Unlimited' : formatCredits(personalBilling.creditsRemaining)}
                          hint={`Period ${personalBilling.period.periodId}`}
                        />
                      </div>
                    </div>
                  </section>
                )}

                <div className="grid gap-3 xl:grid-cols-6">
                  <SummaryStatCard
                    isDarkMode={isDarkMode}
                    icon={<Users className="h-5 w-5" />}
                    label="Members"
                    value={formatNumber(overview.organization.members.length)}
                    hint={canManageOrg ? 'Manage roles and overrides below' : 'Read-only directory'}
                  />
                  <SummaryStatCard
                    isDarkMode={isDarkMode}
                    icon={<Shield className="h-5 w-5" />}
                    label="Your Role"
                    value={roleLabel(overview.accessProfile.membership.role)}
                    hint={canManageOrg ? 'Can manage organization settings' : 'Limited to your accessible surfaces'}
                  />
                  <SummaryStatCard
                    isDarkMode={isDarkMode}
                    icon={<Activity className="h-5 w-5" />}
                    label="Usage Visibility"
                    value={canViewUsage ? 'Enabled' : 'Restricted'}
                    hint={canViewUsage ? 'Organization analytics available' : 'Only personal analytics visible'}
                  />
                  <SummaryStatCard
                    isDarkMode={isDarkMode}
                    icon={<BarChart3 className="h-5 w-5" />}
                    label="Current Month Cost"
                    value={orgBilling ? formatCurrency(orgBilling.totals.totalCostUsd) : formatCurrency(personalBilling?.totalCostUsd || 0)}
                    hint={orgBilling ? 'Across the organization' : 'Your current billing month'}
                  />
                  <SummaryStatCard
                    isDarkMode={isDarkMode}
                    icon={<Activity className="h-5 w-5" />}
                    label="Current Month Credits"
                    value={orgBilling ? formatCredits(orgBilling.totals.totalCreditsUsed) : formatCredits(personalBilling?.totalCreditsUsed || 0)}
                    hint={orgBilling ? 'Across capped members' : 'Your usage this month'}
                  />
                  <SummaryStatCard
                    isDarkMode={isDarkMode}
                    icon={<AlertCircle className="h-5 w-5" />}
                    label="Members Over Limit"
                    value={orgBilling ? formatNumber(orgBilling.totals.overLimitMembers) : personalBilling?.isOverLimit ? '1' : '0'}
                    hint={orgBilling ? 'Needs admin attention' : 'Your current credit posture'}
                  />
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Current month burn"
                    description="Daily tracked cost and credit burn for the active billing period."
                  >
                    <BillingActivityBars
                      isDarkMode={isDarkMode}
                      points={orgBilling?.byDay || personalBilling?.byDay || []}
                    />
                  </SectionPanel>
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Billing-period trend"
                    description="Month-over-month cost and credits."
                  >
                    <BillingMonthBars
                      isDarkMode={isDarkMode}
                      points={orgBilling?.byMonth || personalBilling?.byMonth || []}
                    />
                  </SectionPanel>
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                  <MetricLeaderboardCard
                    isDarkMode={isDarkMode}
                    title="Top spenders"
                    description={canViewUsage ? 'Highest metered members in the active window.' : 'Your own provider mix for the active month.'}
                    items={canViewUsage ? orgUserLeaders : personalProviderLeaders}
                    emptyText={canViewUsage ? 'No organization spend recorded yet.' : 'No personal spend recorded yet.'}
                    primaryLabel="Cost"
                    secondaryLabel="Credits"
                  />
                  <MetricLeaderboardCard
                    isDarkMode={isDarkMode}
                    title="Provider concentration"
                    description="See which providers and services are driving spend."
                    items={canViewUsage ? orgProviderLeaders : personalProviderLeaders}
                    emptyText="No provider usage recorded yet."
                    primaryLabel="Cost"
                    secondaryLabel="Credits"
                  />
                </div>
              </div>
            )}

            {activePane === 'personal' && (
              <div className="space-y-6">
                <SectionPanel
                  isDarkMode={isDarkMode}
                  title="YouTube write access"
                  description="Connect the Google account used for YouTube metadata updates, comments, captions, and analytics."
                >
                  <div className="space-y-4">
                    <div className="grid gap-3 xl:grid-cols-4">
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Status"
                        value={youtubeAuthLoading ? 'Loading…' : youtubeAuthStatus?.authenticated ? 'Connected' : 'Disconnected'}
                        hint={youtubeAuthStatus?.authenticated ? 'Write/auth scopes available to the app' : 'OAuth token missing or expired'}
                      />
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Expiry"
                        value={youtubeAuthStatus?.tokenExpiry ? formatDateTime(youtubeAuthStatus.tokenExpiry) : 'Unknown'}
                        hint={formatExpiryCountdown(youtubeAuthStatus?.tokenExpiry)}
                      />
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Video edits"
                        value={youtubeAuthStatus?.canEditVideos ? 'Enabled' : 'Unavailable'}
                        hint="Required for title/description updates"
                      />
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Comments & captions"
                        value={youtubeAuthStatus?.canPostComments ? 'Enabled' : 'Unavailable'}
                        hint="Also used for managed comments and caption uploads"
                      />
                    </div>

                    {youtubeAuthError ? (
                      <div className={`rounded-2xl border px-4 py-3 text-sm ${
                        isDarkMode
                          ? 'border-red-500/30 bg-red-950/30 text-red-100'
                          : 'border-red-200 bg-red-50 text-red-700'
                      }`}>
                        {youtubeAuthError}
                      </div>
                    ) : null}

                    <div className={`rounded-2xl border px-4 py-3 text-sm ${
                      isDarkMode
                        ? 'border-white/10 bg-white/5 text-slate-200'
                        : 'border-slate-200 bg-slate-50 text-slate-700'
                    }`}>
                      <div className="font-medium">Granted capabilities</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {[
                          { label: 'Read metadata', enabled: !!youtubeAuthStatus?.canReadVideoMetadata },
                          { label: 'Edit videos', enabled: !!youtubeAuthStatus?.canEditVideos },
                          { label: 'Post comments', enabled: !!youtubeAuthStatus?.canPostComments },
                        ].map((capability) => (
                          <span
                            key={capability.label}
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                              capability.enabled
                                ? isDarkMode
                                  ? 'bg-emerald-500/15 text-emerald-200'
                                  : 'bg-emerald-100 text-emerald-700'
                                : isDarkMode
                                  ? 'bg-slate-700 text-slate-300'
                                  : 'bg-slate-200 text-slate-600'
                            }`}
                          >
                            {capability.enabled ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            {capability.label}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => void startYouTubeAuth()}
                        disabled={youtubeAuthBusy}
                        className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-medium transition ${
                          isDarkMode
                            ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400 disabled:opacity-60'
                            : 'bg-brand-navy text-white hover:bg-[#17304b] disabled:opacity-60'
                        }`}
                      >
                        {youtubeAuthBusy ? 'Working…' : youtubeAuthStatus?.authenticated ? 'Reconnect YouTube' : 'Connect YouTube'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void loadYouTubeAuthStatus()}
                        disabled={youtubeAuthLoading || youtubeAuthBusy}
                        className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition ${
                          isDarkMode
                            ? 'border-white/15 bg-transparent text-slate-200 hover:bg-white/5 disabled:opacity-60'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60'
                        }`}
                      >
                        Refresh status
                      </button>
                      {youtubeAuthStatus?.authenticated ? (
                        <button
                          type="button"
                          onClick={() => void disconnectYouTubeAuth()}
                          disabled={youtubeAuthBusy}
                          className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition ${
                            isDarkMode
                              ? 'border-red-500/30 bg-red-950/20 text-red-200 hover:bg-red-950/35 disabled:opacity-60'
                              : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60'
                          }`}
                        >
                          Disconnect
                        </button>
                      ) : null}
                    </div>
                  </div>
                </SectionPanel>

                <div className="grid gap-3 xl:grid-cols-[1fr_1fr_1fr_1fr_1.2fr]">
                  <MiniStatCard
                    isDarkMode={isDarkMode}
                    label="Last used"
                    value={formatRelativeTime(overview.selfUsage.activity?.lastUsedAt || personalBilling?.lastUsageAt)}
                    hint={formatDateTime(overview.selfUsage.activity?.lastUsedAt || personalBilling?.lastUsageAt)}
                  />
                  <MiniStatCard
                    isDarkMode={isDarkMode}
                    label={`${overview.selfUsage.summary.days}d events`}
                    value={formatNumber(overview.selfUsage.summary.totals.events)}
                    hint={overview.selfUsage.activity?.lastUsageAt ? `Tracked usage on ${formatDateTime(overview.selfUsage.activity.lastUsageAt)}` : 'No metered events in the current window'}
                  />
                  <MiniStatCard
                    isDarkMode={isDarkMode}
                    label={`${overview.selfUsage.summary.days}d cost`}
                    value={formatCurrency(overview.selfUsage.summary.totals.total_cost_usd)}
                    hint={`Credits ${formatCredits(overview.selfUsage.summary.totals.total_credits_used || 0)}`}
                  />
                  <MiniStatCard
                    isDarkMode={isDarkMode}
                    label="Current month credits"
                    value={personalBilling?.isUnlimited ? 'Unlimited' : formatCredits(personalBilling?.totalCreditsUsed || 0)}
                    hint={
                      personalBilling?.creditsRemaining === null
                        ? 'No monthly credit cap'
                        : `${formatCredits(personalBilling?.creditsRemaining || 0)} remaining`
                    }
                  />
                  <ActivitySparklineCard
                    isDarkMode={isDarkMode}
                    usage={overview.selfUsage.activity}
                    fallbackDays={activityDays}
                  />
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="My billing-month burn"
                    description="Daily cost and credits for your current billing period."
                  >
                    <BillingActivityBars
                      isDarkMode={isDarkMode}
                      points={personalBilling?.byDay || []}
                    />
                  </SectionPanel>
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="My billing trend"
                    description="Billing periods over time for your account."
                  >
                    <BillingMonthBars
                      isDarkMode={isDarkMode}
                      points={personalBilling?.byMonth || []}
                    />
                  </SectionPanel>
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                  <MetricLeaderboardCard
                    isDarkMode={isDarkMode}
                    title="Provider spend"
                    description="Your current billing month by provider and service."
                    items={personalProviderLeaders}
                    emptyText="No current-month provider usage."
                    primaryLabel="Cost"
                    secondaryLabel="Credits"
                  />
                  <UsageTableCard
                    isDarkMode={isDarkMode}
                    title="Cost status"
                    headers={['Status', 'Events', 'Credits', 'Cost']}
                    emptyText="No current-month status data."
                    rows={(personalBilling?.byStatus || []).map((row) => [
                      usageStatusLabel(row.costStatus),
                      formatNumber(row.events),
                      formatCredits(row.totalCreditsUsed),
                      formatCurrency(row.totalCostUsd),
                    ])}
                  />
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                  <UsageTableCard
                    isDarkMode={isDarkMode}
                    title="Rolling Provider Mix"
                    headers={['Provider', 'Events', 'Units', 'Credits', 'Cost']}
                    emptyText="No recent provider usage."
                    rows={overview.selfUsage.summary.byProvider.map((row) => [
                      `${row.provider}/${row.service}`,
                      formatNumber(row.events),
                      formatNumber(row.total_units),
                      formatCredits(row.total_credits_used || 0),
                      formatCurrency(row.total_cost_usd),
                    ])}
                  />
                  <UsageTableCard
                    isDarkMode={isDarkMode}
                    title="Rolling Cost Status"
                    headers={['Status', 'Events', 'Credits', 'Cost']}
                    emptyText="No recent usage status data."
                    rows={overview.selfUsage.summary.byStatus.map((row) => [
                      usageStatusLabel(row.cost_status),
                      formatNumber(row.events),
                      formatCredits(row.total_credits_used || 0),
                      formatCurrency(row.total_cost_usd),
                    ])}
                  />
                </div>

                <SectionPanel
                  isDarkMode={isDarkMode}
                  title="Recent personal usage"
                  description="Every metered action attributed to your account."
                >
                  {overview.selfUsage.recent.length === 0 ? (
                    <p className={secondaryTextClass}>No recent personal usage events.</p>
                  ) : (
                    <div className="max-h-[420px] overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className={isDarkMode ? 'bg-gray-950/70' : 'bg-white'}>
                          <tr>
                            {['When', 'Operation', 'Units', 'Status', 'Credits', 'Cost', 'Details'].map((header) => (
                              <th
                                key={header}
                                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${subtleTextClass}`}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {overview.selfUsage.recent.map((event) => (
                            <tr key={event.id} className={isDarkMode ? 'bg-transparent' : 'bg-white'}>
                              <td className="px-4 py-3 text-sm">{formatDateTime(event.created_at)}</td>
                              <td className="px-4 py-3 text-sm">
                                <div>{event.provider}/{event.service}</div>
                                <div className={secondaryTextClass}>{event.operation}</div>
                              </td>
                              <td className={`px-4 py-3 text-sm ${secondaryTextClass}`}>
                                {event.total_units ? formatNumber(event.total_units) : event.quantity ? `${formatNumber(event.quantity)} ${event.unit || ''}`.trim() : '0'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium">{usageStatusLabel(event.cost_status)}</div>
                                <div className={secondaryTextClass}>{event.price_source || 'price source unavailable'}</div>
                              </td>
                              <td className="px-4 py-3 text-sm">{formatCredits(event.credits_used || 0)}</td>
                              <td className="px-4 py-3 text-sm">{formatCurrency(event.cost_usd)}</td>
                              <td className={`px-4 py-3 text-sm ${secondaryTextClass}`}>
                                <div>{event.model || 'No model'}</div>
                                <div>{event.resource_type || 'resource'} {event.resource_id ? `· ${event.resource_id}` : ''}</div>
                                <div>
                                  {event.byok ? 'BYOK' : 'Org billed'}
                                  {event.cache_hit ? ' · cache hit' : ''}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionPanel>
              </div>
            )}

            {activePane === 'billing' && (
              <div className="space-y-6">
                {!canViewUsage || !orgBilling ? (
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Organization billing visibility restricted"
                    description="You can still see your own credits in the My Usage pane."
                  >
                    <p className={secondaryTextClass}>
                      Organization-wide billing controls and analytics are only visible to org admins and master users.
                    </p>
                  </SectionPanel>
                ) : (
                  <>
                    <div className="grid gap-3 md:grid-cols-5">
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Shield className="h-5 w-5" />}
                        label="Month Cost"
                        value={formatCurrency(orgBilling.totals.totalCostUsd)}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<BarChart3 className="h-5 w-5" />}
                        label="Credits Used"
                        value={formatCredits(orgBilling.totals.totalCreditsUsed)}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Users className="h-5 w-5" />}
                        label="Remaining Credits"
                        value={orgBilling.totals.remainingCredits === null ? 'Unlimited' : formatCredits(orgBilling.totals.remainingCredits)}
                        hint={orgBilling.totals.remainingCredits === null ? 'No capped members in this organization' : 'Across capped members only'}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Activity className="h-5 w-5" />}
                        label="USD Remaining"
                        value={orgBilling.totals.remainingUsd === null ? 'Unlimited' : formatCurrency(orgBilling.totals.remainingUsd)}
                        hint={orgBilling.totals.remainingUsd === null ? 'No capped members in this organization' : 'Across capped members only'}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<AlertCircle className="h-5 w-5" />}
                        label="Members Over Limit"
                        value={formatNumber(orgBilling.totals.overLimitMembers)}
                      />
                    </div>

                    {billingPolicyDraft && (
                      <SectionPanel
                        isDarkMode={isDarkMode}
                        title="Budget defaults"
                        description="Master users remain unlimited. Everyone else inherits these defaults unless a member override is set."
                      >
                        <div className="grid gap-3 md:grid-cols-4">
                          <LabeledInput
                            label="Default USD / Month"
                            value={String(billingPolicyDraft.defaultMonthlyUsdLimit)}
                            onChange={(value) =>
                              setBillingPolicyDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      defaultMonthlyUsdLimit: normalizeNullableNumber(value) ?? current.defaultMonthlyUsdLimit,
                                    }
                                  : current
                              )
                            }
                            disabled={!canManageOrg}
                            inputClass={inputClass}
                          />
                          <LabeledInput
                            label="Default Credits / Month"
                            value={String(billingPolicyDraft.defaultMonthlyCreditLimit)}
                            onChange={(value) =>
                              setBillingPolicyDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      defaultMonthlyCreditLimit: normalizeNullableNumber(value) ?? current.defaultMonthlyCreditLimit,
                                    }
                                  : current
                              )
                            }
                            disabled={!canManageOrg}
                            inputClass={inputClass}
                          />
                          <LabeledInput
                            label="Credits / USD"
                            value={String(billingPolicyDraft.defaultCreditsPerUsd)}
                            onChange={(value) =>
                              setBillingPolicyDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      defaultCreditsPerUsd: normalizeNullableNumber(value) ?? current.defaultCreditsPerUsd,
                                    }
                                  : current
                              )
                            }
                            disabled={!canManageOrg}
                            inputClass={inputClass}
                          />
                          <LabeledInput
                            label="Warning Threshold %"
                            value={String(billingPolicyDraft.warningThresholdPercent)}
                            onChange={(value) =>
                              setBillingPolicyDraft((current) =>
                                current
                                  ? {
                                      ...current,
                                      warningThresholdPercent:
                                        Math.max(1, Math.min(100, Math.round(normalizeNullableNumber(value) ?? current.warningThresholdPercent))),
                                    }
                                  : current
                              )
                            }
                            disabled={!canManageOrg}
                            inputClass={inputClass}
                          />
                        </div>

                        <div className={`mt-6 rounded-2xl border ${nestedSurfaceClass}`}>
                          <div className="border-b px-4 py-3 dark:border-gray-700">
                            <h4 className="font-medium">Credits per dollar mapping</h4>
                            <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                              These multipliers let you bias in-app credits independently from underlying USD cost.
                            </p>
                          </div>
                          <div className="max-h-[320px] overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className={isDarkMode ? 'bg-gray-900/70' : 'bg-gray-50'}>
                                <tr>
                                  {['Provider', 'Service', 'Operation', 'Credits / USD'].map((header) => (
                                    <th
                                      key={header}
                                      className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${subtleTextClass}`}
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {billingPolicyDraft.serviceCreditRates.map((rate, index) => (
                                  <tr key={`${rate.provider}-${rate.service}-${rate.operation}-${index}`}>
                                    <td className="px-4 py-3 text-sm">{rate.provider}</td>
                                    <td className="px-4 py-3 text-sm">{rate.service}</td>
                                    <td className="px-4 py-3 text-sm">{rate.operation}</td>
                                    <td className="px-4 py-3 text-sm">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={String(rate.creditsPerUsd)}
                                        disabled={!canManageOrg}
                                        onChange={(event) => updateBillingRate(index, event.target.value)}
                                        className={`${inputClass} max-w-[140px]`}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </SectionPanel>
                    )}

                    <div className="grid gap-4 xl:grid-cols-2">
                      <SectionPanel
                        isDarkMode={isDarkMode}
                        title="Current month spend pattern"
                        description="Daily tracked cost and credit burn for the active billing period."
                      >
                        <BillingActivityBars
                          isDarkMode={isDarkMode}
                          points={orgBilling.byDay}
                        />
                      </SectionPanel>
                      <SectionPanel
                        isDarkMode={isDarkMode}
                        title="Month-over-month trend"
                        description={`Last ${orgBilling.byMonth.length} billing periods.`}
                      >
                        <BillingMonthBars
                          isDarkMode={isDarkMode}
                          points={orgBilling.byMonth}
                        />
                      </SectionPanel>
                    </div>

                    <div className="grid gap-4 xl:grid-cols-2">
                      <GroupedMetricLeaderboardCard
                        isDarkMode={isDarkMode}
                        title="Principal breakdown"
                        description="Member accounts are separated from shared viewers, public surfaces, and automation clients."
                        groups={[
                          {
                            title: 'Member accounts',
                            items: orgPrincipalLeaders.memberItems,
                          },
                          {
                            title: 'Shared & system clients',
                            items: orgPrincipalLeaders.otherItems,
                          },
                        ]}
                        emptyText="No principal spend recorded yet."
                        primaryLabel="Cost"
                        secondaryLabel="Credits"
                      />
                      <MetricLeaderboardCard
                        isDarkMode={isDarkMode}
                        title="Provider concentration"
                        description="Which providers are consuming the budget."
                        items={orgProviderLeaders}
                        emptyText="No provider usage recorded yet."
                        primaryLabel="Cost"
                        secondaryLabel="Credits"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {activePane === 'members' && (
              <div className="space-y-6">
                <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Member posture"
                    description="Roles control broad access. Member overrides are optional, and removals stay staged until global save runs."
                  >
                    <div className="grid gap-3 md:grid-cols-4">
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Members"
                        value={formatNumber(overview.organization.members.length)}
                        hint={canManageOrg ? 'Invite, remove, or edit access' : 'Read-only list'}
                      />
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Writers + Admins"
                        value={formatNumber(overview.organization.members.filter((member) => member.role !== 'reader').length)}
                        hint="Can create or manage resources"
                      />
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Over limit"
                        value={formatNumber(overview.organization.members.filter((member) => member.billing?.isOverLimit).length)}
                        hint="Needs credits or override changes"
                      />
                      <MiniStatCard
                        isDarkMode={isDarkMode}
                        label="Recently active"
                        value={formatNumber(overview.organization.members.filter((member) => member.usage?.lastUsedAt || member.lastLoginAt).length)}
                        hint={`Activity window ${activityDays}d`}
                      />
                    </div>
                  </SectionPanel>
                  <MetricLeaderboardCard
                    isDarkMode={isDarkMode}
                    title="Top active members"
                    description="Highest spenders or most recently active accounts."
                    items={memberUsageLeaders}
                    emptyText="No tracked member activity yet."
                    primaryLabel="Cost"
                    secondaryLabel="Credits"
                  />
                </div>

                {canManageOrg && (
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Stage new member"
                    description="Queue invitations and access overrides, then persist them with the global save action."
                  >
                    <div className="grid gap-3 md:grid-cols-3">
                      <input
                        type="email"
                        value={inviteDraft.email}
                        onChange={(event) => setInviteDraft((current) => ({ ...current, email: event.target.value }))}
                        placeholder="member@example.com"
                        className={inputClass}
                      />
                      <input
                        type="text"
                        value={inviteDraft.name}
                        onChange={(event) => setInviteDraft((current) => ({ ...current, name: event.target.value }))}
                        placeholder="Display name"
                        className={inputClass}
                      />
                      <select
                        value={inviteDraft.role}
                        onChange={(event) =>
                          setInviteDraft((current) => ({
                            ...current,
                            role: event.target.value as OrganizationRole,
                          }))
                        }
                        className={inputClass}
                      >
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mt-4">
                      <FeatureModeGrid
                        isDarkMode={isDarkMode}
                        modes={inviteDraft.capabilityModes}
                        onModeChange={updateInviteDraftMode}
                      />
                    </div>

                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={stageInviteDraft}
                        className="inline-flex items-center gap-2 rounded-xl bg-brand-navy px-4 py-2 text-sm font-medium text-brand-cloud transition-colors hover:bg-[#1a2230]"
                      >
                        <Plus className="h-4 w-4" />
                        Stage Member
                      </button>
                    </div>
                  </SectionPanel>
                )}

                {stagedNewMemberList.length > 0 && (
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Staged additions"
                    description="These entries are queued locally until you save."
                  >
                    <div className="space-y-3">
                      {stagedNewMemberList.map((draft) => (
                        <div
                          key={draft.email}
                          className={`rounded-2xl border p-4 ${nestedSurfaceClass}`}
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <p className="font-medium">{draft.name || draft.email}</p>
                              <p className={`text-sm ${secondaryTextClass}`}>{draft.email}</p>
                              <p className={`mt-2 text-xs ${secondaryTextClass}`}>
                                {roleLabel(draft.role)} with{' '}
                                {FEATURE_POLICY_FIELDS.filter(
                                  (feature) => draft.capabilityModes[feature.key] && draft.capabilityModes[feature.key] !== 'inherit'
                                ).length}{' '}
                                explicit override(s)
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => editStagedMember(draft.email)}
                                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                                  isDarkMode
                                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                <Pencil className="h-4 w-4" />
                                Edit Draft
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setStagedNewMembers((current) => {
                                    const next = { ...current };
                                    delete next[draft.email];
                                    return next;
                                  })
                                }
                                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-500"
                              >
                                <X className="h-4 w-4" />
                                Remove Draft
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </SectionPanel>
                )}

                <div className="space-y-4">
                  {overview.organization.members.map((member) => {
                    const draft = memberDrafts[member.email] || buildMemberDraft(member);
                    const billingDraft = memberBillingDrafts[member.email] || buildMemberBillingDraft(member);
                    const isExpanded = !!expandedMembers[member.email];
                    const isLockedMaster = member.role === 'master' && !canGrantMaster;
                    const isPendingRemoval = pendingRemovalSet.has(normalizeDraftEmail(member.email));
                    const isSelf = normalizeDraftEmail(member.email) === normalizeDraftEmail(currentUserEmail);
                    const memberRoleOptions = isLockedMaster
                      ? [...roleOptions, { value: 'master' as const, label: 'Master' }]
                      : roleOptions;
                    const explicitOverrides = FEATURE_POLICY_FIELDS.filter(
                      (feature) => draft.capabilityModes[feature.key] && draft.capabilityModes[feature.key] !== 'inherit'
                    ).length;
                    const usage = member.usage;
                    const billing = member.billing;

                    return (
                      <section
                        key={member.id}
                        className={`rounded-[32px] border p-5 transition-opacity ${nestedSurfaceClass} ${isPendingRemoval ? 'opacity-70' : ''}`}
                      >
                        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                              {member.picture ? (
                                <img
                                  src={member.picture}
                                  alt={member.name}
                                  className="h-12 w-12 rounded-full object-cover"
                                />
                              ) : (
                                <div
                                  className={`flex h-12 w-12 items-center justify-center rounded-full ${
                                    isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  {member.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="truncate text-lg font-semibold">{member.name}</p>
                                <p className={`truncate text-sm ${secondaryTextClass}`}>{member.email}</p>
                              </div>
                            </div>

                            <div className={`mt-3 flex flex-wrap items-center gap-2 text-xs ${secondaryTextClass}`}>
                              <span className={`rounded-full px-2.5 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>
                                {roleLabel(member.role)}
                              </span>
                              <span>Explicit overrides: {explicitOverrides}</span>
                              <span>Updated {formatDateTime(member.updatedAt || member.createdAt)}</span>
                              {usage?.lastUsedAt || member.lastLoginAt ? (
                                <span>Last seen {formatRelativeTime(usage?.lastUsedAt || member.lastLoginAt)}</span>
                              ) : null}
                              {billing?.isUnlimited && (
                                <span className="rounded-full bg-[#e8edf2] px-2.5 py-1 text-brand-navy dark:bg-brand-navy/30 dark:text-brand-cloud">
                                  Unlimited credits
                                </span>
                              )}
                              {billing?.isOverLimit && (
                                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                                  Over monthly limit
                                </span>
                              )}
                              {!billing?.isOverLimit && billing?.isWarning && (
                                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                                  Near monthly limit
                                </span>
                              )}
                              {isLockedMaster && (
                                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                                  Master changes locked to master users
                                </span>
                              )}
                              {isSelf && (
                                <span className="rounded-full bg-[#fff3d6] px-2.5 py-1 text-[#7a560c] dark:bg-brand-yellow/20 dark:text-brand-yellow">
                                  You
                                </span>
                              )}
                              {isPendingRemoval && (
                                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                                  Queued for removal
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {canManageOrg ? (
                              <>
                                <select
                                  value={draft.role}
                                  onChange={(event) =>
                                    updateMemberDraft(member.email, (current) => ({
                                      ...current,
                                      role: event.target.value as OrganizationRole,
                                    }))
                                  }
                                  disabled={isLockedMaster || isPendingRemoval}
                                  className={`${inputClass} min-w-[150px] ${isLockedMaster || isPendingRemoval ? 'cursor-not-allowed opacity-70' : ''}`}
                                >
                                  {memberRoleOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedMembers((current) => ({
                                      ...current,
                                      [member.email]: !current[member.email],
                                    }))
                                  }
                                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                                    isDarkMode
                                      ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  Details
                                </button>
                                <button
                                  type="button"
                                  onClick={() => queueRemoval(member.email)}
                                  disabled={isLockedMaster || isSelf}
                                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                                    isPendingRemoval
                                      ? 'bg-amber-500 text-white hover:bg-amber-400'
                                      : 'bg-red-600 text-white hover:bg-red-500'
                                  } ${(isLockedMaster || isSelf) ? 'cursor-not-allowed opacity-60' : ''}`}
                                >
                                  {isPendingRemoval ? <Undo2 className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                                  {isPendingRemoval ? 'Undo Remove' : isSelf ? 'Cannot Remove Self' : 'Remove'}
                                </button>
                              </>
                            ) : (
                              <div className={`rounded-xl px-3 py-2 text-sm ${nestedSurfaceClass} ${secondaryTextClass}`}>
                                {roleLabel(member.role)}
                              </div>
                            )}
                          </div>
                        </div>

                        {canViewUsage && (
                          <div className="mt-5 grid gap-3 xl:grid-cols-[1fr_1fr_1fr_1fr_1.2fr]">
                            <MiniStatCard
                              isDarkMode={isDarkMode}
                              label="Last used"
                              value={formatRelativeTime(usage?.lastUsedAt || member.lastLoginAt)}
                              hint={formatDateTime(usage?.lastUsedAt || member.lastLoginAt)}
                            />
                            <MiniStatCard
                              isDarkMode={isDarkMode}
                              label={`${usage?.days || usageDays}d events`}
                              value={formatNumber(usage?.events || 0)}
                              hint={usage?.lastUsageAt ? `Tracked usage on ${formatDateTime(usage.lastUsageAt)}` : 'No billed or metered events in window'}
                            />
                            <MiniStatCard
                              isDarkMode={isDarkMode}
                              label={`${usage?.days || usageDays}d cost`}
                              value={formatCurrency(usage?.totalCostUsd || 0)}
                              hint={usage?.lastSessionUsedAt ? `Last authenticated activity ${formatDateTime(usage.lastSessionUsedAt)}` : 'No recent signed activity'}
                            />
                            <MiniStatCard
                              isDarkMode={isDarkMode}
                              label="Month credits"
                              value={billing?.isUnlimited ? 'Unlimited' : formatCredits(billing?.totalCreditsUsed || 0)}
                              hint={
                                billing?.isUnlimited
                                  ? 'No monthly cap'
                                  : billing?.creditsRemaining !== null && billing?.creditsRemaining !== undefined
                                    ? `${formatCredits(billing.creditsRemaining)} remaining`
                                    : 'Unlimited'
                              }
                            />
                            <ActivitySparklineCard
                              isDarkMode={isDarkMode}
                              usage={usage}
                              fallbackDays={activityDays}
                            />
                          </div>
                        )}

                        {(isExpanded || !canManageOrg) && (
                          <div className="mt-5">
                            {billing && (
                              <div className={`mb-4 rounded-2xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-950/50' : 'border-gray-200 bg-white'}`}>
                                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div>
                                    <p className="font-medium">Monthly billing snapshot</p>
                                    <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                                      Period {billing.period.periodId}. Costs and credits reset at the start of each calendar month.
                                    </p>
                                  </div>
                                  <div
                                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                                      billing.isOverLimit
                                        ? isDarkMode
                                          ? 'bg-red-950/50 text-red-200'
                                          : 'bg-red-100 text-red-700'
                                        : billing.isWarning
                                          ? isDarkMode
                                            ? 'bg-amber-950/50 text-amber-200'
                                            : 'bg-amber-100 text-amber-700'
                                          : isDarkMode
                                            ? 'bg-brand-navy/30 text-brand-cloud'
                                            : 'bg-[#e8edf2] text-brand-navy'
                                    }`}
                                  >
                                    {billing.isUnlimited ? 'Unlimited' : billing.isOverLimit ? 'Over limit' : billing.isWarning ? 'Warning threshold' : 'Within limit'}
                                  </div>
                                </div>

                                <div className="grid gap-3 md:grid-cols-4">
                                  <MiniStatCard
                                    isDarkMode={isDarkMode}
                                    label="Month cost"
                                    value={formatCurrency(billing.totalCostUsd)}
                                    hint={billing.monthlyUsdLimit !== null ? `${formatPercent(billing.percentUsdUsed)} of ${formatCurrency(billing.monthlyUsdLimit)}` : 'Unlimited'}
                                  />
                                  <MiniStatCard
                                    isDarkMode={isDarkMode}
                                    label="Month credits"
                                    value={formatCredits(billing.totalCreditsUsed)}
                                    hint={billing.monthlyCreditLimit !== null ? `${formatPercent(billing.percentCreditsUsed)} of ${formatCredits(billing.monthlyCreditLimit)}` : 'Unlimited'}
                                  />
                                  <MiniStatCard
                                    isDarkMode={isDarkMode}
                                    label="Credits left"
                                    value={billing.creditsRemaining === null ? 'Unlimited' : formatCredits(billing.creditsRemaining)}
                                    hint={`Updated ${formatDateTime(billing.lastUsageAt)}`}
                                  />
                                  <MiniStatCard
                                    isDarkMode={isDarkMode}
                                    label="USD left"
                                    value={billing.usdRemaining === null ? 'Unlimited' : formatCurrency(billing.usdRemaining)}
                                    hint={billing.override ? 'Member override active' : 'Using org default'}
                                  />
                                </div>

                                {canManageOrg && (
                                  <div className="mt-4 border-t pt-4 dark:border-gray-700">
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                      <div>
                                        <p className="font-medium">Member override</p>
                                        <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                                          Leave both limits empty to inherit the organization defaults.
                                        </p>
                                      </div>
                                      <label className="inline-flex items-center gap-2 text-sm font-medium">
                                        <input
                                          type="checkbox"
                                          checked={billingDraft.isUnlimited}
                                          disabled={isLockedMaster || isPendingRemoval}
                                          onChange={(event) =>
                                            updateMemberBillingDraft(member.email, (current) => ({
                                              ...current,
                                              isUnlimited: event.target.checked,
                                              monthlyCreditLimit: event.target.checked ? '' : current.monthlyCreditLimit,
                                              monthlyUsdLimit: event.target.checked ? '' : current.monthlyUsdLimit,
                                            }))
                                          }
                                        />
                                        Unlimited
                                      </label>
                                    </div>

                                    <div className="grid gap-3 md:grid-cols-2">
                                      <LabeledInput
                                        label="USD / Month Override"
                                        value={billingDraft.monthlyUsdLimit}
                                        onChange={(value) =>
                                          updateMemberBillingDraft(member.email, (current) => ({
                                            ...current,
                                            monthlyUsdLimit: value,
                                          }))
                                        }
                                        disabled={billingDraft.isUnlimited || isLockedMaster || isPendingRemoval}
                                        placeholder="Inherit"
                                        inputClass={inputClass}
                                      />
                                      <LabeledInput
                                        label="Credits / Month Override"
                                        value={billingDraft.monthlyCreditLimit}
                                        onChange={(value) =>
                                          updateMemberBillingDraft(member.email, (current) => ({
                                            ...current,
                                            monthlyCreditLimit: value,
                                          }))
                                        }
                                        disabled={billingDraft.isUnlimited || isLockedMaster || isPendingRemoval}
                                        placeholder="Inherit"
                                        inputClass={inputClass}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {canManageOrg ? (
                              <FeatureModeGrid
                                isDarkMode={isDarkMode}
                                modes={draft.capabilityModes}
                                onModeChange={(key, value) =>
                                  updateMemberDraft(member.email, (current) => ({
                                    ...current,
                                    capabilityModes: {
                                      ...current.capabilityModes,
                                      [key]: value,
                                    },
                                  }))
                                }
                                disabled={isLockedMaster || isPendingRemoval}
                              />
                            ) : (
                              <ReadOnlyOverrideList
                                isDarkMode={isDarkMode}
                                modes={draft.capabilityModes}
                              />
                            )}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              </div>
            )}

            {activePane === 'policy' && (
              <div className="space-y-6">
                <SectionPanel
                  isDarkMode={isDarkMode}
                  title="Global feature policy"
                  description="Organization policy gates the product surface for every member. Explicit member overrides cannot turn a feature back on if it is disabled here."
                >
                  <div className="mb-6 grid gap-3 md:grid-cols-3">
                    <SummaryStatCard
                      isDarkMode={isDarkMode}
                      icon={<Shield className="h-5 w-5" />}
                      label="Enabled features"
                      value={`${permissionCount(policyDraft || overview.organization.featurePolicy)}/${FEATURE_POLICY_FIELDS.length}`}
                      hint="Global policy before per-member overrides"
                    />
                    <SummaryStatCard
                      isDarkMode={isDarkMode}
                      icon={<Users className="h-5 w-5" />}
                      label="Members inheriting"
                      value={formatNumber(overview.organization.members.length)}
                      hint="All members are affected by this layer"
                    />
                    <SummaryStatCard
                      isDarkMode={isDarkMode}
                      icon={<Activity className="h-5 w-5" />}
                      label="Save model"
                      value={canManageOrg ? 'Global save' : 'Read-only'}
                      hint={canManageOrg ? 'Changes stage until Save All' : 'You can inspect but not edit'}
                    />
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    {FEATURE_POLICY_FIELDS.map((feature) => {
                      const enabled = policyDraft?.[feature.key] ?? false;
                      return (
                        <div
                          key={feature.key}
                          className={`rounded-2xl border p-4 ${nestedSurfaceClass}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="font-medium">{feature.label}</p>
                              <p className={`mt-1 text-sm ${secondaryTextClass}`}>{feature.description}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setPolicyDraft((current) =>
                                  current
                                    ? {
                                        ...current,
                                        [feature.key]: !current[feature.key],
                                      }
                                    : current
                                )
                              }
                              disabled={!canManageOrg || !policyDraft}
                              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                                enabled ? 'bg-brand-yellow' : isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
                              } ${!canManageOrg ? 'cursor-not-allowed opacity-70' : ''}`}
                            >
                              <span
                                className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                                  enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SectionPanel>
              </div>
            )}

            {activePane === 'activity' && (
              <div className="space-y-6">
                {!canViewUsage || !overview.usage ? (
                  <SectionPanel
                    isDarkMode={isDarkMode}
                    title="Organization-wide activity is restricted"
                    description="Your own usage remains available in the My Usage pane."
                  >
                    <p className={secondaryTextClass}>
                      Organization-wide usage visibility is limited to org admins and master users.
                    </p>
                  </SectionPanel>
                ) : (
                  <>
                    <div className="grid gap-3 md:grid-cols-5">
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Activity className="h-5 w-5" />}
                        label="Events"
                        value={formatNumber(overview.usage.summary.totals.events)}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Shield className="h-5 w-5" />}
                        label="Cost"
                        value={formatCurrency(overview.usage.summary.totals.total_cost_usd)}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<BarChart3 className="h-5 w-5" />}
                        label="Credits"
                        value={formatCredits(overview.usage.summary.totals.total_credits_used || 0)}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Users className="h-5 w-5" />}
                        label="Units"
                        value={formatNumber(overview.usage.summary.totals.total_units)}
                      />
                      <SummaryStatCard
                        isDarkMode={isDarkMode}
                        icon={<Activity className="h-5 w-5" />}
                        label="Bytes Out"
                        value={formatBytes(overview.usage.summary.totals.bytes_out)}
                      />
                    </div>

                    <div className="grid gap-4 xl:grid-cols-2">
                      <GroupedMetricLeaderboardCard
                        isDarkMode={isDarkMode}
                        title="Principals"
                        description="Member accounts are separated from shared viewers and automation identities."
                        groups={[
                          {
                            title: 'Member accounts',
                            items: organizationUsagePrincipalLeaders.memberItems,
                          },
                          {
                            title: 'Shared & system clients',
                            items: organizationUsagePrincipalLeaders.otherItems,
                          },
                        ]}
                        emptyText="No principal usage yet."
                        primaryLabel="Cost"
                        secondaryLabel="Credits"
                      />
                      <MetricLeaderboardCard
                        isDarkMode={isDarkMode}
                        title="Operations"
                        description="Most expensive operation families in the selected window."
                        items={overview.usage.summary.byOperation.slice(0, 10).map((row) => ({
                          label: `${row.provider}/${row.service}/${row.operation}`,
                          value: row.total_cost_usd || 0,
                          secondaryValue: row.total_credits_used || 0,
                          detail: `${formatNumber(row.events)} events`,
                        }))}
                        emptyText="No operation usage yet."
                        primaryLabel="Cost"
                        secondaryLabel="Credits"
                      />
                    </div>

                    <div className="grid gap-4 xl:grid-cols-2">
                      <UsageTableCard
                        isDarkMode={isDarkMode}
                        title="By Provider"
                        headers={['Provider', 'Events', 'Units', 'Credits', 'Cost']}
                        emptyText="No provider usage yet."
                        rows={overview.usage.summary.byProvider.map((row) => [
                          `${row.provider}/${row.service}`,
                          formatNumber(row.events),
                          formatNumber(row.total_units),
                          formatCredits(row.total_credits_used || 0),
                          formatCurrency(row.total_cost_usd),
                        ])}
                      />
                      <UsageTableCard
                        isDarkMode={isDarkMode}
                        title="By Status"
                        headers={['Status', 'Events', 'Credits', 'Cost']}
                        emptyText="No cost status data yet."
                        rows={overview.usage.summary.byStatus.map((row) => [
                          usageStatusLabel(row.cost_status),
                          formatNumber(row.events),
                          formatCredits(row.total_credits_used || 0),
                          formatCurrency(row.total_cost_usd),
                        ])}
                      />
                    </div>

                    <SectionPanel
                      isDarkMode={isDarkMode}
                      title="Recent organization usage"
                      description="The latest metered events across the organization."
                    >
                      {overview.usage.recent.length === 0 ? (
                        <p className={secondaryTextClass}>No recent usage events.</p>
                      ) : (
                        <div className="max-h-[480px] overflow-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className={isDarkMode ? 'bg-gray-950/70' : 'bg-white'}>
                              <tr>
                                {['When', 'User', 'Operation', 'Units', 'Status', 'Credits', 'Cost', 'Details'].map((header) => (
                                  <th
                                    key={header}
                                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${subtleTextClass}`}
                                  >
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {overview.usage.recent.map((event) => (
                                <tr key={event.id} className={isDarkMode ? 'bg-transparent' : 'bg-white'}>
                                  <td className="px-4 py-3 text-sm">{formatDateTime(event.created_at)}</td>
                                  <td className={`px-4 py-3 text-sm ${secondaryTextClass}`}>
                                    {event.user_email || 'Unknown'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <div>{event.provider}/{event.service}</div>
                                    <div className={secondaryTextClass}>{event.operation}</div>
                                  </td>
                                  <td className={`px-4 py-3 text-sm ${secondaryTextClass}`}>
                                    {event.total_units ? formatNumber(event.total_units) : event.quantity ? `${formatNumber(event.quantity)} ${event.unit || ''}`.trim() : '0'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <div className="font-medium">{usageStatusLabel(event.cost_status)}</div>
                                    <div className={secondaryTextClass}>{event.price_source || 'price source unavailable'}</div>
                                  </td>
                                  <td className="px-4 py-3 text-sm">{formatCredits(event.credits_used || 0)}</td>
                                  <td className="px-4 py-3 text-sm">{formatCurrency(event.cost_usd)}</td>
                                  <td className={`px-4 py-3 text-sm ${secondaryTextClass}`}>
                                    <div>{event.model || 'No model'}</div>
                                    <div>{event.resource_type || 'resource'} {event.resource_id ? `· ${event.resource_id}` : ''}</div>
                                    <div>
                                      {event.byok ? 'BYOK' : 'Org billed'}
                                      {event.cache_hit ? ' · cache hit' : ''}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </SectionPanel>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

function SettingsNavButton({
  isDarkMode,
  title,
  description,
  badge,
  active,
  onClick,
}: {
  isDarkMode: boolean;
  title: string;
  description: string;
  badge: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
        className={`w-full rounded-2xl border px-4 py-3 text-left transition-all ${
        active
          ? isDarkMode
            ? 'border-brand-yellow/50 bg-brand-yellow/10 text-white shadow-[0_0_0_1px_rgba(255,181,43,0.18)]'
            : 'border-[#e7c572] bg-[#fff6e5] text-gray-900 shadow-[0_0_0_1px_rgba(255,181,43,0.12)]'
          : isDarkMode
            ? 'border-gray-800 bg-gray-950/40 text-gray-100 hover:border-gray-700 hover:bg-gray-900/70'
            : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong className="block text-sm font-semibold">{title}</strong>
          <span className={`mt-1 block text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {description}
          </span>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${active ? 'bg-brand-yellow text-brand-navy' : isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          {badge}
        </span>
      </div>
    </button>
  );
}

function RangeSegmentControl({
  isDarkMode,
  options,
  value,
  onChange,
}: {
  isDarkMode: boolean;
  options: Array<{ value: number; label: string }>;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className={`inline-flex flex-wrap gap-2 rounded-2xl ${isDarkMode ? 'bg-gray-950/50' : 'bg-white'}`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
            option.value === value
              ? 'bg-brand-yellow text-brand-navy'
              : isDarkMode
                ? 'bg-gray-900 text-gray-300 hover:bg-gray-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function SectionPanel({
  isDarkMode,
  title,
  description,
  children,
}: {
  isDarkMode: boolean;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`rounded-[32px] border p-5 ${isDarkMode ? 'border-gray-800 bg-gray-900/50' : 'border-gray-200 bg-gray-50/80'}`}>
      <div className="mb-5 flex flex-col gap-2">
        <h3 className="text-xl font-semibold">{title}</h3>
        {description ? <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function MetricLeaderboardCard({
  isDarkMode,
  title,
  description,
  items,
  emptyText,
  primaryLabel,
  secondaryLabel,
}: {
  isDarkMode: boolean;
  title: string;
  description?: string;
  items: Array<{
    label: string;
    value: number;
    secondaryValue?: number;
    detail?: string;
  }>;
  emptyText: string;
  primaryLabel: string;
  secondaryLabel?: string;
}) {
  const maxValue = items.reduce((max, item) => Math.max(max, item.value), 0);
  const formatSecondaryValue = (value: number) =>
    secondaryLabel?.toLowerCase() === 'credits' ? formatCredits(value) : formatNumber(value);

  return (
    <SectionPanel
      isDarkMode={isDarkMode}
      title={title}
      description={description}
    >
      {items.length === 0 ? (
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{emptyText}</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const width = maxValue > 0 ? Math.max(8, Math.round((item.value / maxValue) * 100)) : 8;
            return (
              <div key={`${item.label}-${item.value}`} className="space-y-2">
                <div className="flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.label}</p>
                    {item.detail ? (
                      <p className={`mt-0.5 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.detail}</p>
                    ) : null}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{formatCurrency(item.value)}</p>
                    {secondaryLabel && item.secondaryValue !== undefined ? (
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {formatSecondaryValue(item.secondaryValue)} {secondaryLabel.toLowerCase()}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className={`h-2.5 overflow-hidden rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-brand-navy via-[#41526a] to-brand-yellow"
                    style={{ width: `${width}%` }}
                    title={`${primaryLabel}: ${formatCurrency(item.value)}${secondaryLabel && item.secondaryValue !== undefined ? ` · ${secondaryLabel}: ${formatSecondaryValue(item.secondaryValue)}` : ''}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionPanel>
  );
}

function GroupedMetricLeaderboardCard({
  isDarkMode,
  title,
  description,
  groups,
  emptyText,
  primaryLabel,
  secondaryLabel,
}: {
  isDarkMode: boolean;
  title: string;
  description?: string;
  groups: Array<{
    title: string;
    items: Array<{
      label: string;
      value: number;
      secondaryValue?: number;
      detail?: string;
    }>;
  }>;
  emptyText: string;
  primaryLabel: string;
  secondaryLabel?: string;
}) {
  const visibleGroups = groups.filter((group) => group.items.length > 0);
  const maxValue = visibleGroups.reduce(
    (max, group) => Math.max(max, ...group.items.map((item) => item.value), 0),
    0,
  );
  const formatSecondaryValue = (value: number) =>
    secondaryLabel?.toLowerCase() === 'credits' ? formatCredits(value) : formatNumber(value);

  return (
    <SectionPanel isDarkMode={isDarkMode} title={title} description={description}>
      {visibleGroups.length === 0 ? (
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{emptyText}</p>
      ) : (
        <div className="space-y-5">
          {visibleGroups.map((group) => (
            <div key={group.title} className="space-y-4">
              <div className="border-b border-dashed border-gray-200 pb-2 dark:border-gray-700">
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {group.title}
                </p>
              </div>
              {group.items.map((item) => {
                const width = maxValue > 0 ? Math.max(8, Math.round((item.value / maxValue) * 100)) : 8;
                return (
                  <div key={`${group.title}-${item.label}-${item.value}`} className="space-y-2">
                    <div className="flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{item.label}</p>
                        {item.detail ? (
                          <p className={`mt-0.5 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.detail}</p>
                        ) : null}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{formatCurrency(item.value)}</p>
                        {secondaryLabel && item.secondaryValue !== undefined ? (
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {formatSecondaryValue(item.secondaryValue)} {secondaryLabel.toLowerCase()}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className={`h-2.5 overflow-hidden rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-brand-navy via-[#41526a] to-brand-yellow"
                        style={{ width: `${width}%` }}
                        title={`${primaryLabel}: ${formatCurrency(item.value)}${secondaryLabel && item.secondaryValue !== undefined ? ` · ${secondaryLabel}: ${formatSecondaryValue(item.secondaryValue)}` : ''}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </SectionPanel>
  );
}

function SummaryStatCard({
  icon,
  isDarkMode,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  isDarkMode: boolean;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-gray-50'}`}>
      <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700'}`}>
        {icon}
      </div>
      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
      {hint ? <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{hint}</p> : null}
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  disabled = false,
  placeholder,
  inputClass,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputClass: string;
}) {
  return (
    <label className="block">
      <p className="mb-2 text-sm font-medium">{label}</p>
      <input
        type="number"
        min="0"
        step="0.1"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={inputClass}
      />
    </label>
  );
}

function MiniStatCard({
  isDarkMode,
  label,
  value,
  hint,
}: {
  isDarkMode: boolean;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-950/40' : 'border-gray-200 bg-white'}`}>
      <p className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{label}</p>
      <p className="mt-2 text-lg font-semibold">{value}</p>
      {hint ? <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{hint}</p> : null}
    </div>
  );
}

function ActivitySparklineCard({
  isDarkMode,
  usage,
  fallbackDays,
}: {
  isDarkMode: boolean;
  usage?: OrganizationMemberUsage | null;
  fallbackDays: number;
}) {
  const points = usage?.recentActivity || [];
  const maxEvents = points.reduce((max, point) => Math.max(max, point.events), 0);

  return (
    <div className={`rounded-2xl border p-4 ${isDarkMode ? 'border-gray-700 bg-gray-950/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Activity pattern
          </p>
          <p className="mt-2 text-sm font-medium">
            {points.length ? `${points.length} day view` : `No events in last ${fallbackDays} days`}
          </p>
        </div>
        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Peak {formatNumber(maxEvents)}
        </div>
      </div>
      <div className="mt-4 flex h-16 items-end gap-1">
        {(points.length ? points : Array.from({ length: fallbackDays }, () => ({ date: '', events: 0, totalCostUsd: 0 }))).map((point, index) => {
          const height = maxEvents > 0 ? Math.max(12, Math.round((point.events / maxEvents) * 100)) : 12;
          return (
            <div key={`${point.date || 'empty'}-${index}`} className="flex flex-1 flex-col items-center gap-2">
              <div
                className={`w-full rounded-t ${isDarkMode ? 'bg-brand-yellow/70' : 'bg-brand-navy'} ${point.events === 0 ? 'opacity-25' : ''}`}
                style={{ height: `${height}%` }}
                title={point.date ? `${point.date}: ${point.events} events` : 'No activity'}
              />
            </div>
          );
        })}
      </div>
      <div className={`mt-2 flex items-center justify-between text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
        <span>{points[0]?.date || 'Earlier'}</span>
        <span>{points[points.length - 1]?.date || 'Now'}</span>
      </div>
    </div>
  );
}

function BillingActivityBars({
  isDarkMode,
  points,
}: {
  isDarkMode: boolean;
  points: Array<{ date: string; events: number; totalCostUsd: number; totalCreditsUsed: number }>;
}) {
  const safePoints = points.length
    ? points
    : Array.from({ length: 7 }, (_, index) => ({
        date: `day-${index}`,
        events: 0,
        totalCostUsd: 0,
        totalCreditsUsed: 0,
      }));
  const maxCost = safePoints.reduce((max, point) => Math.max(max, point.totalCostUsd), 0);
  const maxCredits = safePoints.reduce((max, point) => Math.max(max, point.totalCreditsUsed), 0);

  return (
    <div className="mt-4">
      <div className="flex h-40 items-end gap-2">
        {safePoints.map((point, index) => {
          const costHeight = maxCost > 0 ? Math.max(10, Math.round((point.totalCostUsd / maxCost) * 100)) : 10;
          const creditsHeight = maxCredits > 0 ? Math.max(10, Math.round((point.totalCreditsUsed / maxCredits) * 100)) : 10;
          return (
            <div key={`${point.date}-${index}`} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex h-full w-full items-end justify-center gap-1">
                <div
                  className={`w-full rounded-t ${isDarkMode ? 'bg-brand-navy/80' : 'bg-brand-navy'} ${point.totalCostUsd === 0 ? 'opacity-25' : ''}`}
                  style={{ height: `${costHeight}%` }}
                  title={`${point.date}: ${formatCurrency(point.totalCostUsd)} cost`}
                />
                <div
                  className={`w-full rounded-t ${isDarkMode ? 'bg-brand-yellow/80' : 'bg-brand-yellow'} ${point.totalCreditsUsed === 0 ? 'opacity-25' : ''}`}
                  style={{ height: `${creditsHeight}%` }}
                  title={`${point.date}: ${formatCredits(point.totalCreditsUsed)} credits`}
                />
              </div>
              <div className={`text-center text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {point.date === `day-${index}` ? ' ' : point.date.slice(5)}
              </div>
            </div>
          );
        })}
      </div>
      <div className={`mt-3 flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${isDarkMode ? 'bg-brand-navy/80' : 'bg-brand-navy'}`} />
          Cost
        </span>
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${isDarkMode ? 'bg-brand-yellow/80' : 'bg-brand-yellow'}`} />
          Credits
        </span>
      </div>
    </div>
  );
}

function BillingMonthBars({
  isDarkMode,
  points,
}: {
  isDarkMode: boolean;
  points: Array<{ periodId: string; events: number; totalCostUsd: number; totalCreditsUsed: number }>;
}) {
  const safePoints = points.length
    ? points
    : Array.from({ length: 6 }, (_, index) => ({
        periodId: `month-${index}`,
        events: 0,
        totalCostUsd: 0,
        totalCreditsUsed: 0,
      }));
  const maxCost = safePoints.reduce((max, point) => Math.max(max, point.totalCostUsd), 0);
  const maxCredits = safePoints.reduce((max, point) => Math.max(max, point.totalCreditsUsed), 0);

  return (
    <div className="mt-4">
      <div className="flex h-40 items-end gap-2">
        {safePoints.map((point, index) => {
          const costHeight = maxCost > 0 ? Math.max(10, Math.round((point.totalCostUsd / maxCost) * 100)) : 10;
          const creditsHeight = maxCredits > 0 ? Math.max(10, Math.round((point.totalCreditsUsed / maxCredits) * 100)) : 10;
          return (
            <div key={`${point.periodId}-${index}`} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex h-full w-full items-end justify-center gap-1">
                <div
                  className={`w-full rounded-t ${isDarkMode ? 'bg-brand-navy/80' : 'bg-brand-navy'} ${point.totalCostUsd === 0 ? 'opacity-25' : ''}`}
                  style={{ height: `${costHeight}%` }}
                  title={`${point.periodId}: ${formatCurrency(point.totalCostUsd)} cost`}
                />
                <div
                  className={`w-full rounded-t ${isDarkMode ? 'bg-brand-yellow/80' : 'bg-brand-yellow'} ${point.totalCreditsUsed === 0 ? 'opacity-25' : ''}`}
                  style={{ height: `${creditsHeight}%` }}
                  title={`${point.periodId}: ${formatCredits(point.totalCreditsUsed)} credits`}
                />
              </div>
              <div className={`text-center text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {point.periodId === `month-${index}` ? ' ' : point.periodId.slice(2)}
              </div>
            </div>
          );
        })}
      </div>
      <div className={`mt-3 flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${isDarkMode ? 'bg-brand-navy/80' : 'bg-brand-navy'}`} />
          Cost
        </span>
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${isDarkMode ? 'bg-brand-yellow/80' : 'bg-brand-yellow'}`} />
          Credits
        </span>
      </div>
    </div>
  );
}

function FeatureModeGrid({
  isDarkMode,
  modes,
  onModeChange,
  disabled = false,
}: {
  isDarkMode: boolean;
  modes: Partial<Record<FeatureKey, FeatureOverrideMode>>;
  onModeChange: (key: FeatureKey, value: FeatureOverrideMode) => void;
  disabled?: boolean;
}) {
  const selectClass = `w-full rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-yellow ${
    isDarkMode
      ? 'border-gray-600 bg-gray-900 text-gray-100'
      : 'border-gray-300 bg-white text-gray-900'
  } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {FEATURE_POLICY_FIELDS.map((feature) => (
        <label key={feature.key} className={`rounded-2xl border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-white'}`}>
          <div className="mb-2">
            <p className="text-sm font-medium">{feature.label}</p>
            <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{feature.description}</p>
          </div>
          <select
            value={modes[feature.key] || 'inherit'}
            onChange={(event) => onModeChange(feature.key, event.target.value as FeatureOverrideMode)}
            disabled={disabled}
            className={selectClass}
          >
            <option value="inherit">Inherit org policy</option>
            <option value="allow">Allow</option>
            <option value="disable">Disable</option>
          </select>
        </label>
      ))}
    </div>
  );
}

function ReadOnlyOverrideList({
  isDarkMode,
  modes,
}: {
  isDarkMode: boolean;
  modes: Partial<Record<FeatureKey, FeatureOverrideMode>>;
}) {
  const explicitModes = FEATURE_POLICY_FIELDS.filter(
    (feature) => modes[feature.key] && modes[feature.key] !== 'inherit'
  );

  if (explicitModes.length === 0) {
    return (
      <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/60 text-gray-400' : 'border-gray-200 bg-white text-gray-600'}`}>
        No explicit member overrides. This user inherits the organization policy.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {explicitModes.map((feature) => {
        const mode = modes[feature.key];
        return (
          <span
            key={feature.key}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              mode === 'allow'
                ? isDarkMode
                  ? 'bg-brand-yellow/15 text-brand-yellow'
                  : 'bg-[#fff3d6] text-[#7a560c]'
                : isDarkMode
                  ? 'bg-red-950/40 text-red-300'
                  : 'bg-red-100 text-red-700'
            }`}
          >
            {feature.label}: {mode === 'allow' ? 'Allowed' : 'Disabled'}
          </span>
        );
      })}
    </div>
  );
}

function UsageTableCard({
  isDarkMode,
  title,
  headers,
  rows,
  emptyText,
}: {
  isDarkMode: boolean;
  title: string;
  headers: string[];
  rows: string[][];
  emptyText: string;
}) {
  return (
    <div className={`rounded-2xl border ${isDarkMode ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-gray-50'}`}>
      <div className="border-b px-4 py-3 dark:border-gray-700">
        <h4 className="font-medium">{title}</h4>
      </div>
      {rows.length === 0 ? (
        <div className="p-4">
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{emptyText}</p>
        </div>
      ) : (
        <div className="overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={isDarkMode ? 'bg-gray-950/70' : 'bg-white'}>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header}
                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((row, rowIndex) => (
                <tr key={`${title}-${rowIndex}`} className={isDarkMode ? 'bg-transparent' : 'bg-gray-50'}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${title}-${rowIndex}-${cellIndex}`} className="px-4 py-3 text-sm">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PermissionSettingsPage;
